import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-open-rate');

/**
 * The third article in cluster D, added beyond the original two-page plan for two reasons: a
 * two-article cluster leaves its lone spoke with only two incoming internal links (index + pillar),
 * which fails the ≥3 rule the cluster spec enforces; and the AI subject-line tools had no learn
 * counterpart at all. Both problems have the same answer, and this is a subject most published
 * guides still get wrong by quoting pre-2021 benchmarks as if nothing changed.
 */
export default function EmailOpenRatePage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'An open is recorded when a tracking pixel in the message loads. Since Apple Mail Privacy Protection began pre-loading images for Apple Mail users whether or not anyone reads the message, a large share of recorded opens never happened — so open rate is now a mix of real reads and machine fetches with no way to separate them.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="How an open was ever measured">
        <p>
          There is no protocol event for "a person read this". The number has always been inferred
          from a single-pixel image embedded in the HTML:
        </p>
        <LearnCode>
          {`<img src="https://track.example-esp.com/o/abc123.gif" width="1" height="1" />

the image loads  → an open is recorded
the image does not load → nothing is recorded`}
        </LearnCode>
        <p>
          Two failure modes were baked in from the start, long before Apple. A reader with images
          off is never counted, so the number always undercounted. And a plain-text message cannot
          carry a pixel at all.
        </p>
      </LearnSection>

      <LearnSection title="What Apple MPP changed">
        <p>
          Mail Privacy Protection, introduced with iOS 15 in 2021 and on by default, routes images
          through Apple's proxy and <strong className="text-txt">pre-loads them</strong> — fetching
          the pixel when the message arrives, regardless of whether the recipient ever opens it.
        </p>
        <LearnTable
          head={['', 'Before MPP', 'With MPP']}
          rows={[
            ['Pixel loads when', 'The reader opens the message', <Lead key="a">The message arrives</Lead>],
            ['Recorded open means', 'Probably read', <Lead key="b">Delivered. Nothing more</Lead>],
            ['Open timestamp', 'Roughly when they read it', 'Roughly when it arrived'],
            ['Reported IP / location', 'The reader’s', 'Apple’s proxy — geography is fiction'],
            ['Device reported', 'The reader’s', 'Masked'],
          ]}
        />
        <p>
          The direction of the error is what makes it awkward: MPP inflates the number rather than
          suppressing it. Open rates rose across the industry in late 2021 and many teams read it as
          improved performance. It was a measurement change.
        </p>
        <p>
          Gmail's image proxy is often mentioned in the same breath and is a different thing — it
          caches images but fetches on actual open, so a Gmail open is still an open. It costs you
          device and location accuracy, not the event itself.
        </p>
      </LearnSection>

      <LearnSection title="What open rate is still good for">
        <p>Not nothing — just much less than it was, and only in specific shapes.</p>
        <LearnTable
          head={['Use', 'Still valid?', 'Why']}
          rows={[
            ['Comparing two subject lines in one A/B split', 'Partly', 'MPP contaminates both arms roughly equally, so a large gap still means something'],
            ['Tracking your own trend over time', <Lead key="a">Yes, if the segment is stable</Lead>, 'The inflation is roughly constant month to month'],
            ['Comparing against an industry benchmark', <Lead key="b">No</Lead>, 'Different Apple share, different inflation. The numbers are not comparable'],
            ['Deciding who is engaged, for suppression', <Lead key="c">No</Lead>, 'You would keep mailing people who never opened anything'],
            ['Proving a human read it', <Lead key="d">No</Lead>, 'That is precisely what MPP removed'],
          ]}
        />
        <p>
          Row four is the expensive one. Suppressing on engagement is the single most effective
          thing you can do for{' '}
          <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
            sender reputation
          </Link>
          , and a suppression rule keyed on opens now retains a large group of people who never read
          anything — exactly the group whose disengagement is dragging you down.
        </p>
      </LearnSection>

      <LearnSection title="What to measure instead">
        <LearnList>
          <li>
            <Lead>Clicks.</Lead> A click requires a deliberate action, and no privacy proxy
            manufactures one. The obvious replacement, and it works.
          </li>
          <li>
            <Lead>Replies.</Lead> The strongest signal available and the most under-used. Also
            weighted positively by mailbox providers, which opens are not.
          </li>
          <li>
            <Lead>Conversions.</Lead> The thing you actually wanted. Harder to attribute, immune to
            all of this.
          </li>
          <li>
            <Lead>Click-to-delivered</Lead> rather than click-to-open. Click-to-open divides by a
            denominator MPP has corrupted, so it inherits the problem.
          </li>
          <li>
            <Lead>Complaint and unsubscribe rate.</Lead> Both are real recipient actions, and both
            are what providers watch.
          </li>
          <li>
            <Lead>Google Postmaster Tools.</Lead> First-party Gmail data — reputation, complaint
            rate, delivery errors — none of it derived from a pixel.
          </li>
        </LearnList>
      </LearnSection>

      <LearnSection title="Fixing an engagement-suppression rule">
        <LearnSubsection title="The rule most people still run">
          <p>
            "Suppress anyone who has not opened in six months." Since MPP, a chunk of that
            population is being counted as active on the strength of an automated fetch, so they
            stay on the list and keep depressing your real engagement rate.
          </p>
        </LearnSubsection>
        <LearnSubsection title="A version that survives MPP">
          <p>
            Suppress on <em>no click and no reply</em> in the window, with opens as a tiebreaker at
            most. Stricter, and it removes the people who genuinely are not reading — which is the
            entire point of the exercise.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Expect the reported numbers to get worse">
          <p>
            Your engagement rate will look lower after this change because the denominator gets
            honest, and your{' '}
            <Link to="/learn/email-bounce-rate" className="text-accent underline underline-offset-4">
              list size
            </Link>{' '}
            will drop. Both are improvements. Deliverability follows the real rate, not the reported
            one, so the reported one getting worse while placement improves is the expected shape.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'Why are email open rates unreliable now?',
    a: 'Apple Mail Privacy Protection pre-loads tracking pixels when a message arrives, whether or not the recipient ever opens it. Since it is on by default for Apple Mail users, a large share of recorded opens are machine fetches with no way to tell them from real reads.',
  },
  {
    q: 'What is Apple Mail Privacy Protection?',
    a: 'A feature introduced with iOS 15 in 2021, enabled by default, that routes message images through Apple’s proxy and fetches them on arrival. It masks the reader’s IP and device and makes the open event mean "delivered" rather than "read".',
  },
  {
    q: 'Does Gmail break open tracking the same way?',
    a: 'No. Gmail proxies and caches images but fetches them when the message is actually opened, so a Gmail open is still an open. It costs you device and location accuracy, not the event itself. The two are often conflated and should not be.',
  },
  {
    q: 'What should I measure instead of opens?',
    a: 'Clicks and replies, because both require deliberate action no proxy manufactures. Then conversions, complaint rate and unsubscribe rate. Use click-to-delivered rather than click-to-open, since click-to-open divides by the corrupted number.',
  },
  {
    q: 'Is open rate completely useless now?',
    a: 'No, but its uses are narrow. A large gap in a single A/B split still means something because both arms are contaminated equally, and your own trend over a stable segment is readable. Comparing to an industry benchmark is not, because Apple share differs.',
  },
  {
    q: 'Should I still suppress inactive subscribers based on opens?',
    a: 'Not on opens alone — that rule now retains people MPP counted as active who never read anything, which is the group dragging your engagement down. Suppress on no click and no reply in the window, with opens as a tiebreaker at most.',
  },
];
